
import { DEFAULT_DECIMAL_PLACES } from 'Config';
import { numericFormatter } from 'react-number-format';
import { DetailedCellError, HyperFormula } from 'hyperformula';
import i18n from 'common/services/I18n';

export type FormatType = 'plain' | 'money' | 'percentage';

export interface ExpressionResult {
    value: number;
    error: boolean;
}

export interface ExpressionParameter {
    id: string;
    name: string;
    value: string | number | null;
}

export const EXPRESSION_PARAMETER_MENTION_PREFIX = 'M_';
export const EXPRESSION_PARAMETER_MENTION_SUFFIX = '_E';

class ExpressionSystem {
    public formatNumber(val: string | null | undefined, decimalScale?: number, type?: FormatType) {
        return numericFormatter(val || '0', {
            decimalScale: decimalScale ?? DEFAULT_DECIMAL_PLACES,
            suffix: type === 'plain' ? '' : (type === 'percentage' ? ' %' : ' €'),
            thousandSeparator: ' ',
            decimalSeparator: ',',
            fixedDecimalScale: true,
        })
    }

    private strToNumber(str: string | null | undefined) {
        const parsedValue = Number(str || 0);
        return isNaN(parsedValue) ? 0 : parsedValue;
    }

    public numberToStr (value: number | null | undefined) {
        return value?.toString() || '0';
    }

    private isExpressionValid(expr: string) {
        if (!isNaN((expr as unknown) as number) && expr.trim() !== '') return true;

        return expr.trim().startsWith('=');
    }

    public serializeParameterMentionId(id: string) {
        return EXPRESSION_PARAMETER_MENTION_PREFIX + id.replace(/[^0-9A-Za-z_]/g, '') + EXPRESSION_PARAMETER_MENTION_SUFFIX;
    }

    public formatParameterName(names: string[]) {
        return names.join(' > ');
    }

    public formatParameterId(names: string[]) {
        return names.join('__');
    }

    public evaluate(expression: string, expressionParameters: ExpressionParameter[]): ExpressionResult {
        try {
            let expr = (expression ?? '0').toString().trim();

            if (!expr) {
                expr = '0';
            }

            expr = `=${expr}`;

            if (!this.isExpressionValid(expr)) {
                console.error('Invalid input: Only numbers or expressions allowed.');
                return {
                    value: 0,
                    error: true
                };
            }

            const hf = HyperFormula.buildEmpty({
                licenseKey: 'gpl-v3',
                language: `${i18n.language}${i18n.language.toUpperCase()}`,
            });
            const sheetName = hf.addSheet('ExpressionSheet');
            const sheetId = hf.getSheetId(sheetName);

            for (const exprParam of expressionParameters) {
                hf.addNamedExpression(this.serializeParameterMentionId(exprParam.id), exprParam.value);
            }

            hf.setCellContents({ sheet: sheetId!, row: 0, col: 0 }, [['result', expr]]);

            const result = hf.getCellValue({ sheet: sheetId!, row: 0, col: 1 });

            if (result instanceof DetailedCellError) {
                console.error('Error evaluating expression', result);
                return {
                    value: 0,
                    error: true
                };
            }

            return {
                value: this.strToNumber(result?.toString()) ?? 0,
                error: false
            };
        } catch (error) {
            console.error('Error evaluating expression', error);
            return {
                value: 0,
                error: true
            };
        }
    }
}

export default new ExpressionSystem();
